import React, { useCallback, useEffect, useState } from 'react';
import { ymdLocal } from '../lib/dates';
import api from '../api';

interface OutputInvoice {
  id: string;
  invoice_number: string | null;
  customer_name: string | null;
  customer_ico: string | null;
  issue_date: string | null;
  due_date: string | null;
  total_amount: string | number | null;
  currency: string | null;
  item_count: number;
  items_missing_account: number;
}

interface OutputItem {
  id: string;
  description: string;
  quantity: string | number | null;
  unit: string | null;
  unit_price: string | number | null;
  total_price: string | number | null;
  vat_rate: string | number | null;
  account_code: string | null;
}

interface ChartAccount {
  code: string;
  name: string;
}

interface Props {
  onClose: () => void;
}

const fmtEur = (v: any): string => {
  if (v === null || v === undefined || v === '') return '—';
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isNaN(n) ? '—' : `${n.toFixed(2)} €`;
};

const firstDayOfYear = () => ymdLocal(new Date(new Date().getFullYear(), 0, 1));
const today = () => ymdLocal(new Date());

const OutputInvoicesManager: React.FC<Props> = ({ onClose }) => {
  const [from, setFrom] = useState(firstDayOfYear());
  const [to, setTo] = useState(today());
  const [invoices, setInvoices] = useState<OutputInvoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<OutputInvoice | null>(null);
  const [items, setItems] = useState<OutputItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [revenues, setRevenues] = useState<ChartAccount[]>([]);
  const [savingItemId, setSavingItemId] = useState<string | null>(null);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/classification/output-invoices', { params: { from, to } });
      setInvoices(res.data || []);
    } catch (e) {
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  useEffect(() => {
    api.get('/classification/chart')
      .then(res => setRevenues(res.data?.revenues || []))
      .catch(() => setRevenues([]));
  }, []);

  const openInvoice = async (inv: OutputInvoice) => {
    setSelected(inv);
    setItemsLoading(true);
    try {
      const res = await api.get(`/classification/output-invoices/${inv.id}/items`);
      setItems(res.data || []);
    } catch {
      setItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  const updateItemAccount = async (itemId: string, accountCode: string) => {
    setSavingItemId(itemId);
    const oldItem = items.find(it => it.id === itemId);
    const wasMissing = !oldItem?.account_code;
    const willBeMissing = !accountCode;
    try {
      await api.patch(`/classification/output-invoice-items/${itemId}`, { account_code: accountCode || null });
      setItems(prev => prev.map(it => it.id === itemId ? { ...it, account_code: accountCode || null } : it));
      // Lokálna aktualizácia missing count v ľavom zozname — nezachvejeme scroll
      if (selected && wasMissing !== willBeMissing) {
        const delta = wasMissing ? -1 : 1; // missing klesá ak sa práve vyplnil, rastie ak sa vymazal
        setInvoices(prev => prev.map(inv =>
          inv.id === selected.id
            ? { ...inv, items_missing_account: Math.max(0, inv.items_missing_account + delta) }
            : inv
        ));
      }
    } catch {
      // tichá chyba — neuložené ostane v UI
    } finally {
      setSavingItemId(null);
    }
  };

  const [autofilling, setAutofilling] = useState(false);
  const [autofillMessage, setAutofillMessage] = useState<string | null>(null);
  const runAutofill = async () => {
    if (!selected) return;
    setAutofilling(true);
    setAutofillMessage(null);
    try {
      const res = await api.post(`/classification/output-invoices/${selected.id}/autofill`);
      const filled = Number(res.data?.filled || 0);
      setAutofillMessage(`Doplnené ${filled} z ${res.data?.scanned || 0} položiek.`);
      // Refresh items v pravom paneli
      const itemsRes = await api.get(`/classification/output-invoices/${selected.id}/items`);
      setItems(itemsRes.data || []);
      // Lokálne uprav missing count v ľavom zozname (neresetuje scroll)
      if (filled > 0) {
        setInvoices(prev => prev.map(inv =>
          inv.id === selected.id
            ? { ...inv, items_missing_account: Math.max(0, inv.items_missing_account - filled) }
            : inv
        ));
      }
    } catch {
      setAutofillMessage('Chyba pri auto-doplnení.');
    } finally {
      setAutofilling(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1050, padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 8,
          width: '95vw', maxWidth: 1100, height: '88vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        }}
      >
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Vystavené faktúry — účtovné priradenie</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280', lineHeight: 1 }}>×</button>
        </div>

        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Ľavý panel — zoznam faktúr */}
          <div style={{ flex: '0 0 45%', borderRight: '1px solid #e5e7eb', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: 14, borderBottom: '1px solid #f3f4f6', display: 'flex', gap: 8, alignItems: 'end' }}>
              <div style={{ flex: 1 }}>
                <label style={smallLabel}>Od</label>
                <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={smallLabel}>Do</label>
                <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {loading ? (
                <div style={{ padding: 14, color: '#6b7280', fontSize: 13 }}>Načítavam…</div>
              ) : invoices.length === 0 ? (
                <div style={{ padding: 14, color: '#6b7280', fontSize: 13, fontStyle: 'italic' }}>Žiadne faktúry v rozsahu.</div>
              ) : invoices.map(inv => (
                <div
                  key={inv.id}
                  onClick={() => openInvoice(inv)}
                  style={{
                    padding: 12,
                    borderBottom: '1px solid #f3f4f6',
                    cursor: 'pointer',
                    background: selected?.id === inv.id ? '#eff6ff' : 'transparent',
                  }}
                  onMouseEnter={e => { if (selected?.id !== inv.id) (e.currentTarget.style.background = '#f9fafb'); }}
                  onMouseLeave={e => { if (selected?.id !== inv.id) (e.currentTarget.style.background = 'transparent'); }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{inv.invoice_number || '(bez čísla)'}</div>
                      <div style={{ color: '#374151', fontSize: 13, marginTop: 2 }}>{inv.customer_name || '—'}</div>
                      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 2 }}>
                        {inv.issue_date ? new Date(inv.issue_date).toLocaleDateString('sk-SK') : ''} · {inv.item_count} pol.
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 600, fontSize: 13 }}>{fmtEur(inv.total_amount)}</div>
                      {inv.items_missing_account > 0 && (
                        <div style={{ marginTop: 4, padding: '2px 6px', background: '#fef3c7', color: '#92400e', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                          {inv.items_missing_account} bez účtu
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Pravý panel — položky vybranej faktúry */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
            {!selected ? (
              <div style={{ color: '#6b7280', fontSize: 13 }}>Vyber faktúru zo zoznamu.</div>
            ) : (
              <>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 16, fontWeight: 600 }}>{selected.invoice_number}</div>
                    <div style={{ color: '#6b7280', fontSize: 13 }}>{selected.customer_name}{selected.customer_ico ? ` · IČO ${selected.customer_ico}` : ''}</div>
                  </div>
                  <button
                    onClick={runAutofill}
                    disabled={autofilling || items.length === 0}
                    title="Aplikuje učenie z minulých faktúr + heuristiky pre položky bez účtu"
                    style={{
                      padding: '6px 12px',
                      background: '#fff', color: '#2563eb', border: '1px solid #2563eb',
                      borderRadius: 6, cursor: autofilling ? 'wait' : 'pointer', fontSize: 13,
                      whiteSpace: 'nowrap', opacity: autofilling ? 0.6 : 1,
                    }}
                  >
                    {autofilling ? 'Doplňujem…' : 'Auto-doplniť účty'}
                  </button>
                </div>
                {autofillMessage && (
                  <div style={{ marginBottom: 12, padding: 8, background: '#eff6ff', color: '#1e40af', borderRadius: 4, fontSize: 12 }}>
                    {autofillMessage}
                  </div>
                )}
                {itemsLoading ? (
                  <div style={{ color: '#6b7280', fontSize: 13 }}>Načítavam položky…</div>
                ) : items.length === 0 ? (
                  <div style={{ color: '#6b7280', fontSize: 13, fontStyle: 'italic' }}>Faktúra nemá žiadne položky.</div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                        <th style={th}>Popis</th>
                        <th style={{ ...th, textAlign: 'right', whiteSpace: 'nowrap' }}>Suma</th>
                        <th style={{ ...th, width: 250 }}>Výnosový účet</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map(it => (
                        <tr key={it.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={td}>
                            <div style={{ fontWeight: 500 }}>{it.description}</div>
                            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                              {it.quantity} {it.unit || ''} · DPH {it.vat_rate || 0}%
                            </div>
                          </td>
                          <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                            {fmtEur(it.total_price)}
                          </td>
                          <td style={td}>
                            <select
                              value={it.account_code || ''}
                              onChange={e => updateItemAccount(it.id, e.target.value)}
                              disabled={savingItemId === it.id}
                              style={{
                                width: '100%', padding: '6px 8px',
                                border: it.account_code ? '1px solid #d1d5db' : '1px solid #f59e0b',
                                background: it.account_code ? '#fff' : '#fef3c7',
                                borderRadius: 4, fontSize: 13,
                              }}
                            >
                              <option value="">— vyber účet —</option>
                              {revenues.map(r => (
                                <option key={r.code} value={r.code}>{r.code} — {r.name}</option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const smallLabel: React.CSSProperties = {
  display: 'block', fontSize: 11, fontWeight: 600, color: '#374151',
  textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 4,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '6px 8px', border: '1px solid #d1d5db',
  borderRadius: 4, fontSize: 13, boxSizing: 'border-box',
};
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontWeight: 600, fontSize: 12,
  color: '#374151', textTransform: 'uppercase', letterSpacing: 0.3,
};
const td: React.CSSProperties = { padding: '10px', verticalAlign: 'top' };

export default OutputInvoicesManager;
